import type { QuickPrompt, Agent, Workflow, CapabilityModule } from '@/types/aiToolkit';
import { COMMUNICATION_DEFAULT_AGENTS } from './communicationDefaultAgents';

export const CAPABILITY_MODULES: CapabilityModule[] = [
  {
    id: 'cap-research',
    type: 'research',
    name: 'Research',
    description: 'Deep research capabilities with multi-source synthesis and citation tracking',
    icon: 'Search',
    color: 'bg-blue-500',
    config: {
      maxSteps: 5,
      sources: ['web', 'academic', 'news'],
      outputFormat: 'structured'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-analysis',
    type: 'analysis',
    name: 'Analysis',
    description: 'Critical analysis, pattern recognition, and insight extraction',
    icon: 'TrendingUp',
    color: 'bg-purple-500',
    config: {
      outputFormat: 'detailed'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-writing',
    type: 'writing',
    name: 'Writing',
    description: 'Content creation, editing, and style refinement',
    icon: 'Pencil',
    color: 'bg-green-500',
    config: {
      outputFormat: 'prose'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-coding',
    type: 'coding',
    name: 'Coding',
    description: 'Code generation, debugging, and technical implementation',
    icon: 'Code',
    color: 'bg-orange-500',
    config: {
      outputFormat: 'code'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-web-search',
    type: 'web-search',
    name: 'Web Search',
    description: 'Real-time web search with source verification',
    icon: 'Globe',
    color: 'bg-cyan-500',
    config: {
      sources: ['google', 'bing'],
      outputFormat: 'list'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-summarization',
    type: 'summarization',
    name: 'Summarization',
    description: 'Content condensation and key point extraction',
    icon: 'FileText',
    color: 'bg-teal-500',
    config: {
      outputFormat: 'bullets'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-fact-checking',
    type: 'fact-checking',
    name: 'Fact Checking',
    description: 'Verification of claims and cross-reference validation',
    icon: 'CheckCircle',
    color: 'bg-red-500',
    config: {
      outputFormat: 'verification'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-translation',
    type: 'translation',
    name: 'Translation',
    description: 'Multi-language translation with context preservation',
    icon: 'Languages',
    color: 'bg-indigo-500',
    config: {
      outputFormat: 'bilingual'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-data-processing',
    type: 'data-processing',
    name: 'Data Processing',
    description: 'Data transformation, cleaning, and formatting',
    icon: 'Database',
    color: 'bg-yellow-500',
    config: {
      outputFormat: 'structured'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-document-parsing',
    type: 'document-parsing',
    name: 'Document Parsing',
    description: 'Extract and structure information from documents',
    icon: 'FileSearch',
    color: 'bg-amber-500',
    config: {
      outputFormat: 'structured'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-github-repo',
    type: 'github-repo',
    name: 'GitHub Repository',
    description: 'Access and analyze GitHub repositories - structure, code, dependencies, and documentation',
    icon: 'Github',
    color: 'bg-gray-800',
    config: {
      outputFormat: 'structured',
      sources: ['tree', 'files', 'dependencies', 'readme']
    },
    isBuiltIn: true
  },
  {
    id: 'cap-ui-audit',
    type: 'ui-audit',
    name: 'UI/UX Audit',
    description: 'Analyze frontend components, styling patterns, and user experience flows',
    icon: 'Layout',
    color: 'bg-pink-500',
    config: {
      outputFormat: 'detailed'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-market-research',
    type: 'market-research',
    name: 'Market Research',
    description: 'Research competitors, market positioning, and industry trends',
    icon: 'TrendingUp',
    color: 'bg-emerald-500',
    config: {
      sources: ['web', 'news', 'product-hunt'],
      outputFormat: 'structured'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-gmail-write',
    type: 'custom',
    name: 'Gmail Write',
    description: 'Trash, archive, and draft emails via Gmail API',
    icon: 'Mail',
    color: 'bg-red-500',
    config: {
      operations: ['trash', 'archive', 'create_draft']
    },
    isBuiltIn: true
  },
  {
    id: 'cap-clickup-write',
    type: 'custom',
    name: 'ClickUp Write',
    description: 'Create and update ClickUp tasks from agent output',
    icon: 'ListChecks',
    color: 'bg-violet-500',
    config: {
      operations: ['create_task', 'update_task']
    },
    isBuiltIn: true
  }
];

export const QUICK_PROMPTS: QuickPrompt[] = [
  {
    id: 'qp-recipe-format',
    name: 'Recipe Formatter',
    description: 'Format any recipe into a clean, organized structure with ingredients, instructions, and tips.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'ChefHat',
    color: 'bg-orange-500',
    category: 'Personal',
    tags: ['cooking', 'recipe', 'format'],
    promptTemplate: 'Please format the following recipe into a clean, organized structure:\n\n{{recipe_text}}\n\nInclude:\n- Recipe name and description\n- Prep time and cook time\n- Servings\n- Ingredients list (with measurements)\n- Step-by-step instructions\n- Tips and variations (if applicable)',
    variables: [
      {
        id: 'recipe_text',
        name: 'Recipe Text',
        placeholder: 'Paste your recipe text here...',
        description: 'The raw recipe content to format',
        required: true
      }
    ],
    outputFormat: 'markdown',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'qp-greek-word-study',
    name: 'Greek Word Study',
    description: 'Deep dive into the meaning, usage, and context of Greek biblical words.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'BookOpen',
    color: 'bg-indigo-500',
    category: 'Academic',
    tags: ['bible', 'greek', 'word-study', 'theology'],
    promptTemplate: 'Conduct a comprehensive Greek word study for "{{greek_word}}":\n\n1. **Transliteration & Pronunciation**\n2. **Strong\'s Number** (if applicable)\n3. **Root & Etymology**\n4. **Primary Meanings** in Classical and Koine Greek\n5. **New Testament Usage** - key verses and contexts\n6. **Related Words** in the same word family\n7. **Theological Significance**\n8. **Practical Application** for modern readers',
    variables: [
      {
        id: 'greek_word',
        name: 'Greek Word',
        placeholder: 'e.g., agape',
        description: 'The Greek word to study',
        required: true
      }
    ],
    outputFormat: 'markdown',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'qp-email-professional',
    name: 'Professional Email Writer',
    description: 'Transform your rough ideas into polished, professional emails.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'Mail',
    color: 'bg-blue-500',
    category: 'Business',
    tags: ['email', 'professional', 'writing', 'communication'],
    promptTemplate: 'Write a professional email based on these details:\n\nRecipient: {{recipient}}\nPurpose: {{purpose}}\nKey Points: {{key_points}}\nTone: {{tone}}\n\nProvide a complete email with subject line, greeting, body, and sign-off.',
    variables: [
      {
        id: 'recipient',
        name: 'Recipient',
        placeholder: 'e.g., My manager, A client, HR department',
        required: true
      },
      {
        id: 'purpose',
        name: 'Purpose',
        placeholder: 'e.g., Request time off, Follow up on meeting, Propose new project',
        required: true
      },
      {
        id: 'key_points',
        name: 'Key Points',
        placeholder: 'Main points to include...',
        required: true
      },
      {
        id: 'tone',
        name: 'Tone',
        placeholder: 'e.g., Formal, Friendly, Urgent',
        defaultValue: 'Professional and friendly',
        required: false
      }
    ],
    outputFormat: 'text',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'qp-summary',
    name: 'Quick Summarizer',
    description: 'Summarize any content into key points and takeaways.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'FileText',
    color: 'bg-teal-500',
    category: 'Productivity',
    tags: ['summary', 'notes', 'quick'],
    promptTemplate: 'Summarize the following content:\n\n{{content}}\n\nProvide:\n1. **One-Sentence Summary**\n2. **Key Points** (bullet list)\n3. **Main Takeaways** (2-3 actionable insights)',
    variables: [
      {
        id: 'content',
        name: 'Content',
        placeholder: 'Paste the content to summarize...',
        required: true
      }
    ],
    outputFormat: 'markdown',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'qp-explain-like-5',
    name: 'Explain Like I\'m 5',
    description: 'Break down complex topics into simple, easy-to-understand explanations.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'Lightbulb',
    color: 'bg-yellow-500',
    category: 'Learning',
    tags: ['explain', 'simple', 'learning'],
    promptTemplate: 'Explain {{topic}} like I\'m 5 years old. Use simple words, fun analogies, and relatable examples.',
    variables: [
      {
        id: 'topic',
        name: 'Topic',
        placeholder: 'e.g., How does the internet work?',
        required: true
      }
    ],
    outputFormat: 'text',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'qp-brainstorm',
    name: 'Idea Brainstormer',
    description: 'Generate creative ideas and approaches for any challenge.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'Sparkles',
    color: 'bg-pink-500',
    category: 'Creative',
    tags: ['ideas', 'brainstorm', 'creative'],
    promptTemplate: 'Brainstorm creative ideas for: {{challenge}}\n\nGenerate 10 diverse ideas ranging from practical to bold. For each idea, include a brief description and potential impact.',
    variables: [
      {
        id: 'challenge',
        name: 'Challenge',
        placeholder: 'Describe what you need ideas for...',
        required: true
      }
    ],
    outputFormat: 'markdown',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'qp-swot',
    name: 'Quick SWOT Analysis',
    description: 'Rapid SWOT analysis for any business or personal decision.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'Target',
    color: 'bg-red-500',
    category: 'Business',
    tags: ['swot', 'analysis', 'strategy'],
    promptTemplate: 'Conduct a SWOT analysis for: {{subject}}\n\nProvide:\n- **Strengths**: Internal advantages\n- **Weaknesses**: Internal limitations\n- **Opportunities**: External factors to leverage\n- **Threats**: External risks to mitigate',
    variables: [
      {
        id: 'subject',
        name: 'Subject',
        placeholder: 'e.g., My startup idea, Career change decision',
        required: true
      }
    ],
    outputFormat: 'markdown',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'qp-compare',
    name: 'Quick Comparison',
    description: 'Compare two options with pros, cons, and recommendation.',
    tier: 'quick-prompt',
    access: 'free',
    icon: 'Scale',
    color: 'bg-amber-500',
    category: 'Analysis',
    tags: ['compare', 'decision', 'pros-cons'],
    promptTemplate: 'Compare {{option_a}} vs {{option_b}}:\n\n1. Key differences\n2. Pros and cons of each\n3. Best use cases for each\n4. Your recommendation based on {{criteria}}',
    variables: [
      {
        id: 'option_a',
        name: 'Option A',
        placeholder: 'First option',
        required: true
      },
      {
        id: 'option_b',
        name: 'Option B',
        placeholder: 'Second option',
        required: true
      },
      {
        id: 'criteria',
        name: 'Criteria',
        placeholder: 'e.g., Budget, ease of use',
        defaultValue: 'general use',
        required: false
      }
    ],
    outputFormat: 'markdown',
    createdAt: '2024-01-01',
    createdBy: 'system'
  }
];

export const AGENTS: Agent[] = [
  {
    id: 'agent-research-assistant',
    name: 'Research Assistant',
    description: 'Thorough researcher that finds, analyzes, and synthesizes information from multiple sources.',
    tier: 'agent',
    access: 'free',
    icon: 'Search',
    color: 'bg-blue-500',
    category: 'Research',
    tags: ['research', 'analysis', 'sources'],
    nodeType: 'researcher',
    persona: {
      role: 'Research Assistant',
      expertise: ['Academic research', 'Data synthesis', 'Source evaluation'],
      communicationStyle: 'Thorough and analytical with clear citations',
      systemPrompt: 'You are a meticulous research assistant. Your job is to find relevant information, evaluate source quality, and synthesize findings into clear, well-organized reports. Always cite your sources and highlight areas of uncertainty.'
    },
    capabilityModules: ['cap-research', 'cap-web-search', 'cap-summarization'],
    memoryPolicy: { type: 'session', includeContext: true },
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-writing-editor',
    name: 'Writing Editor',
    description: 'Expert editor that refines writing for clarity, style, and impact.',
    tier: 'agent',
    access: 'free',
    icon: 'Pencil',
    color: 'bg-purple-500',
    category: 'Writing',
    tags: ['writing', 'editing', 'style'],
    nodeType: 'specialist',
    persona: {
      role: 'Writing Editor',
      expertise: ['Grammar and style', 'Clarity improvement', 'Tone adjustment'],
      communicationStyle: 'Constructive and encouraging with specific suggestions',
      systemPrompt: 'You are an expert writing editor. Your job is to improve text for clarity, flow, and impact while preserving the author\'s voice. Provide specific, actionable suggestions and explain your reasoning.'
    },
    capabilityModules: ['cap-writing'],
    memoryPolicy: { type: 'session', includeContext: true },
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-devil-advocate',
    name: 'Devil\'s Advocate',
    description: 'Challenges assumptions and identifies weaknesses in arguments or plans.',
    tier: 'agent',
    access: 'pro',
    icon: 'Scale',
    color: 'bg-red-500',
    category: 'Analysis',
    tags: ['critical-thinking', 'debate', 'analysis'],
    nodeType: 'validator',
    persona: {
      role: 'Devil\'s Advocate',
      expertise: ['Critical analysis', 'Logical reasoning', 'Risk identification'],
      communicationStyle: 'Challenging but respectful, focused on strengthening ideas',
      systemPrompt: 'You are a devil\'s advocate. Your job is to find weaknesses, challenge assumptions, and identify potential problems. Be thorough but constructive - your goal is to make ideas stronger, not to tear them down.'
    },
    capabilityModules: ['cap-analysis', 'cap-fact-checking'],
    memoryPolicy: { type: 'session', includeContext: true },
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-project-orchestrator',
    name: 'Project Orchestrator',
    description: 'Coordinates complex tasks by delegating to specialized agents and synthesizing results.',
    tier: 'agent',
    access: 'pro',
    icon: 'Crown',
    color: 'bg-yellow-500',
    category: 'Productivity',
    tags: ['orchestration', 'coordination', 'management'],
    nodeType: 'orchestrator',
    persona: {
      role: 'Project Orchestrator',
      expertise: ['Task delegation', 'Workflow coordination', 'Result synthesis'],
      communicationStyle: 'Clear and directive with focus on outcomes',
      systemPrompt: 'You are a project orchestrator. Your job is to break down complex tasks, delegate to appropriate specialists, coordinate the workflow, and synthesize final results. Ensure quality at each step and maintain clear communication.'
    },
    capabilityModules: ['cap-analysis', 'cap-summarization'],
    memoryPolicy: { type: 'persistent', retentionDays: 30, includeContext: true },
    collaborationRules: {
      canDelegate: true,
      canReceiveDelegation: false,
      handoffProtocol: 'structured',
      sharedContextKeys: ['projectGoal', 'constraints', 'timeline']
    },
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-fact-checker',
    name: 'Fact Checker',
    description: 'Verifies claims, checks sources, and ensures accuracy of information.',
    tier: 'agent',
    access: 'free',
    icon: 'CheckCircle',
    color: 'bg-green-500',
    category: 'Research',
    tags: ['verification', 'accuracy', 'sources'],
    nodeType: 'validator',
    persona: {
      role: 'Fact Checker',
      expertise: ['Claim verification', 'Source checking', 'Accuracy assessment'],
      communicationStyle: 'Precise and evidence-based with clear verdicts',
      systemPrompt: 'You are a fact checker. Your job is to verify claims, check sources, and assess the accuracy of information. Provide clear verdicts (confirmed, unconfirmed, false, or partially true) with supporting evidence.'
    },
    capabilityModules: ['cap-fact-checking', 'cap-web-search', 'cap-research'],
    memoryPolicy: { type: 'session', includeContext: true },
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-synthesizer',
    name: 'Synthesizer',
    description: 'Combines information from multiple sources into coherent, comprehensive outputs.',
    tier: 'agent',
    access: 'pro',
    icon: 'Layers',
    color: 'bg-purple-600',
    category: 'Analysis',
    tags: ['synthesis', 'aggregation', 'summary'],
    nodeType: 'aggregator',
    persona: {
      role: 'Information Synthesizer',
      expertise: ['Information integration', 'Pattern recognition', 'Comprehensive summaries'],
      communicationStyle: 'Clear and comprehensive with logical organization',
      systemPrompt: 'You are an information synthesizer. Your job is to take multiple inputs, identify common themes and contradictions, and produce coherent, comprehensive outputs that capture the full picture.'
    },
    capabilityModules: ['cap-summarization', 'cap-analysis'],
    memoryPolicy: { type: 'session', includeContext: true },
    collaborationRules: {
      canDelegate: false,
      canReceiveDelegation: true,
      sharedContextKeys: ['inputSources', 'outputFormat']
    },
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-code-assistant',
    name: 'Code Assistant',
    description: 'Writes, reviews, and debugs code across multiple programming languages.',
    tier: 'agent',
    access: 'free',
    icon: 'Code2',
    color: 'bg-orange-500',
    category: 'Technical',
    tags: ['coding', 'programming', 'development'],
    nodeType: 'specialist',
    persona: {
      role: 'Code Assistant',
      expertise: ['Multiple programming languages', 'Code review', 'Debugging', 'Best practices'],
      communicationStyle: 'Technical but clear, with code examples and explanations',
      systemPrompt: 'You are a code assistant. Your job is to help write, review, and debug code. Provide clean, well-documented code following best practices. Explain your reasoning and suggest improvements.'
    },
    capabilityModules: ['cap-coding'],
    memoryPolicy: { type: 'session', includeContext: true },
    preferredModel: 'anthropic/claude-3.5-sonnet',
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-translator',
    name: 'Translator',
    description: 'Translates content between languages while preserving meaning and tone.',
    tier: 'agent',
    access: 'free',
    icon: 'Languages',
    color: 'bg-indigo-500',
    category: 'Writing',
    tags: ['translation', 'language', 'localization'],
    nodeType: 'specialist',
    persona: {
      role: 'Translator',
      expertise: ['Multiple languages', 'Cultural context', 'Tone preservation'],
      communicationStyle: 'Precise with attention to nuance',
      systemPrompt: 'You are a translator. Your job is to translate content between languages while preserving the original meaning, tone, and cultural context. Highlight any phrases that may not translate directly and offer alternatives.'
    },
    capabilityModules: ['cap-translation'],
    memoryPolicy: { type: 'none' },
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-repo-strategist',
    name: 'Repository Strategist',
    description: 'Orchestrates comprehensive GitHub repository analysis by coordinating specialist agents.',
    tier: 'agent',
    access: 'pro',
    icon: 'Crown',
    color: 'bg-gray-800',
    category: 'Technical',
    tags: ['github', 'repository', 'orchestration', 'analysis'],
    nodeType: 'orchestrator',
    persona: {
      role: 'Repository Strategist',
      expertise: ['Project architecture', 'Code organization', 'Technical strategy', 'Team coordination'],
      communicationStyle: 'Strategic and comprehensive with actionable insights',
      systemPrompt: `You are a Repository Strategist. Your job is to orchestrate comprehensive analysis of GitHub repositories by coordinating specialist agents and synthesizing their findings into actionable intelligence.

When analyzing a repository:
1. First understand the project scope and technology stack
2. Delegate structure analysis to the Structure Analyst
3. Delegate code quality review to the Code Reviewer
4. Delegate UI/UX analysis to the UI/UX Auditor (for frontend projects)
5. Delegate competitive research to the Market Researcher
6. Delegate improvement ideas to the Feature Ideator
7. Synthesize all findings into a comprehensive strategic report

Format your final output with clear sections using "KEYWORD: descriptive phrase" headings.`
    },
    capabilityModules: ['cap-github-repo', 'cap-analysis', 'cap-summarization'],
    memoryPolicy: { type: 'persistent', retentionDays: 30, includeContext: true },
    collaborationRules: {
      canDelegate: true,
      canReceiveDelegation: false,
      handoffProtocol: 'structured',
      sharedContextKeys: ['repoUrl', 'repoOwner', 'repoName', 'techStack', 'projectGoal']
    },
    composedAgentIds: ['agent-structure-analyst', 'agent-code-reviewer', 'agent-ui-auditor', 'agent-market-researcher', 'agent-feature-ideator'],
    preferredModel: 'anthropic/claude-3.5-sonnet',
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-structure-analyst',
    name: 'Structure Analyst',
    description: 'Analyzes repository architecture, folder structure, dependencies, and project organization.',
    tier: 'agent',
    access: 'free',
    icon: 'FolderTree',
    color: 'bg-blue-600',
    category: 'Technical',
    tags: ['github', 'architecture', 'structure', 'dependencies'],
    nodeType: 'specialist',
    persona: {
      role: 'Structure Analyst',
      expertise: ['Project architecture', 'Dependency analysis', 'Code organization', 'Build systems'],
      communicationStyle: 'Technical and detailed with clear diagrams',
      systemPrompt: `You are a Structure Analyst specializing in repository architecture. Your job is to analyze:

1. **Folder Structure**: Map the directory hierarchy, identify key directories (src, lib, components, etc.)
2. **Dependencies**: Parse package.json, requirements.txt, Cargo.toml, etc. for dependencies and their versions
3. **Configuration**: Identify build tools, linters, CI/CD configs, and their purposes
4. **Entry Points**: Find main entry files and understand the application bootstrap process
5. **Module Organization**: Identify how code is split into modules/packages

Provide a structured analysis with:
- ARCHITECTURE: Overview of the project structure
- DEPENDENCIES: Key dependencies and their purposes
- BUILD: Build system and tooling
- PATTERNS: Architectural patterns used (monorepo, microservices, etc.)`
    },
    capabilityModules: ['cap-github-repo', 'cap-analysis'],
    memoryPolicy: { type: 'session', includeContext: true },
    collaborationRules: {
      canDelegate: false,
      canReceiveDelegation: true
    },
    preferredModel: 'anthropic/claude-3.5-sonnet',
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-code-reviewer',
    name: 'Code Reviewer',
    description: 'Reviews code quality, patterns, best practices, and identifies technical debt.',
    tier: 'agent',
    access: 'free',
    icon: 'Code2',
    color: 'bg-orange-600',
    category: 'Technical',
    tags: ['github', 'code-review', 'quality', 'patterns'],
    nodeType: 'specialist',
    persona: {
      role: 'Code Reviewer',
      expertise: ['Code quality', 'Design patterns', 'Best practices', 'Technical debt', 'Security'],
      communicationStyle: 'Constructive and specific with code examples',
      systemPrompt: `You are a Code Reviewer analyzing repository code. Your job is to evaluate:

1. **Code Quality**: Naming conventions, modularity, readability, DRY principles
2. **Design Patterns**: Identify patterns used and suggest improvements
3. **Best Practices**: Framework-specific best practices and industry standards
4. **Technical Debt**: Areas needing refactoring, outdated patterns, complexity issues
5. **Security**: Potential vulnerabilities, unsafe practices, exposed secrets

Provide structured feedback:
- QUALITY: Overall code quality assessment (1-10 scale with justification)
- PATTERNS: Design patterns identified and recommendations
- TECH_DEBT: Technical debt items prioritized by severity
- SECURITY: Security concerns if any
- IMPROVEMENTS: Top 5 actionable improvements`
    },
    capabilityModules: ['cap-github-repo', 'cap-coding', 'cap-analysis'],
    memoryPolicy: { type: 'session', includeContext: true },
    collaborationRules: {
      canDelegate: false,
      canReceiveDelegation: true
    },
    preferredModel: 'anthropic/claude-3.5-sonnet',
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-ui-auditor',
    name: 'UI/UX Auditor',
    description: 'Audits frontend components, styling patterns, accessibility, and user experience flows.',
    tier: 'agent',
    access: 'pro',
    icon: 'Layout',
    color: 'bg-pink-600',
    category: 'Technical',
    tags: ['github', 'ui', 'ux', 'frontend', 'design'],
    nodeType: 'specialist',
    persona: {
      role: 'UI/UX Auditor',
      expertise: ['Frontend architecture', 'Component design', 'CSS/Styling', 'Accessibility', 'UX patterns'],
      communicationStyle: 'Visual and user-focused with concrete suggestions',
      systemPrompt: `You are a UI/UX Auditor analyzing frontend code. Your job is to evaluate:

1. **Component Architecture**: Component hierarchy, reusability, prop patterns
2. **Styling System**: CSS organization, design tokens, theming approach
3. **Accessibility**: ARIA labels, keyboard navigation, color contrast
4. **UX Patterns**: User flows, interaction patterns, loading states, error handling
5. **Responsive Design**: Mobile-first approach, breakpoint usage

Provide structured audit:
- COMPONENTS: Component organization and reusability score
- STYLING: CSS architecture and consistency
- ACCESSIBILITY: A11y compliance and issues
- UX_PATTERNS: User experience quality and suggestions
- RECOMMENDATIONS: Top improvements for better UX`
    },
    capabilityModules: ['cap-github-repo', 'cap-ui-audit', 'cap-analysis'],
    memoryPolicy: { type: 'session', includeContext: true },
    collaborationRules: {
      canDelegate: false,
      canReceiveDelegation: true
    },
    preferredModel: 'anthropic/claude-3.5-sonnet',
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-market-researcher',
    name: 'Market Researcher',
    description: 'Researches competitors, market positioning, and industry trends for the project.',
    tier: 'agent',
    access: 'pro',
    icon: 'TrendingUp',
    color: 'bg-emerald-600',
    category: 'Business',
    tags: ['github', 'market', 'competitors', 'positioning'],
    nodeType: 'researcher',
    persona: {
      role: 'Market Researcher',
      expertise: ['Competitive analysis', 'Market trends', 'Product positioning', 'Industry research'],
      communicationStyle: 'Business-focused with data-driven insights',
      systemPrompt: `You are a Market Researcher analyzing the competitive landscape for a project. Your job is to:

1. **Identify Competitors**: Find similar products/projects based on the repo's purpose
2. **Feature Comparison**: Compare features against competitors
3. **Market Trends**: Identify relevant industry trends and opportunities
4. **Positioning**: Suggest unique value propositions and positioning
5. **Growth Opportunities**: Identify underserved niches and expansion possibilities

Provide market analysis:
- COMPETITORS: Top 5 competitors with brief descriptions
- COMPARISON: Feature comparison matrix
- TRENDS: Relevant market trends
- POSITIONING: Unique value proposition suggestions
- OPPORTUNITIES: Growth and differentiation opportunities`
    },
    capabilityModules: ['cap-market-research', 'cap-web-search', 'cap-research'],
    memoryPolicy: { type: 'session', includeContext: true },
    collaborationRules: {
      canDelegate: false,
      canReceiveDelegation: true
    },
    preferredModel: 'openai/gpt-4o',
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'agent-feature-ideator',
    name: 'Feature Ideator',
    description: 'Generates feature ideas, improvements, and a prioritized product roadmap.',
    tier: 'agent',
    access: 'pro',
    icon: 'Lightbulb',
    color: 'bg-yellow-500',
    category: 'Business',
    tags: ['github', 'features', 'roadmap', 'product'],
    nodeType: 'specialist',
    persona: {
      role: 'Feature Ideator',
      expertise: ['Product thinking', 'Feature prioritization', 'Roadmap planning', 'User needs analysis'],
      communicationStyle: 'Creative and strategic with clear prioritization',
      systemPrompt: `You are a Feature Ideator generating improvement ideas for a project. Based on code analysis and market context, your job is to:

1. **Gap Analysis**: Identify missing features compared to competitors
2. **Improvement Ideas**: Generate ideas to enhance existing features
3. **Innovation**: Suggest novel features that could differentiate the product
4. **Technical Improvements**: Developer experience and code quality improvements
5. **Prioritization**: Rank features by impact vs effort

Provide feature roadmap:
- QUICK_WINS: Low-effort, high-impact improvements
- SHORT_TERM: Features for next 1-3 months
- MEDIUM_TERM: Features for 3-6 months
- LONG_TERM: Strategic features for 6+ months
- TECH_IMPROVEMENTS: Developer experience enhancements`
    },
    capabilityModules: ['cap-analysis', 'cap-research'],
    memoryPolicy: { type: 'session', includeContext: true },
    collaborationRules: {
      canDelegate: false,
      canReceiveDelegation: true
    },
    preferredModel: 'anthropic/claude-3.5-sonnet',
    isTemplate: true,
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  ...COMMUNICATION_DEFAULT_AGENTS,
];

export const SAMPLE_WORKFLOWS: Workflow[] = [
  {
    id: 'workflow-deep-research',
    name: 'Deep Research Pipeline',
    description: 'Multi-step research workflow: gather sources, analyze, fact-check, and synthesize.',
    tier: 'workflow',
    access: 'pro',
    icon: 'GitBranch',
    color: 'bg-blue-500',
    category: 'Research',
    tags: ['research', 'multi-step', 'comprehensive'],
    trigger: { type: 'manual', config: {} },
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 50, y: 150 },
        data: { label: 'Start Research', description: 'Begin research on topic' }
      },
      {
        id: 'agent-1',
        type: 'agent',
        position: { x: 250, y: 150 },
        data: { label: 'Research', agentId: 'agent-research-assistant', description: 'Gather information' }
      },
      {
        id: 'agent-2',
        type: 'agent',
        position: { x: 450, y: 150 },
        data: { label: 'Fact Check', agentId: 'agent-fact-checker', description: 'Verify findings' }
      },
      {
        id: 'agent-3',
        type: 'agent',
        position: { x: 650, y: 150 },
        data: { label: 'Synthesize', agentId: 'agent-synthesizer', description: 'Create final report' }
      },
      {
        id: 'output-1',
        type: 'output',
        position: { x: 850, y: 150 },
        data: { label: 'Final Report', outputFormat: 'markdown' }
      }
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-1' },
      { id: 'e2', source: 'agent-1', target: 'agent-2' },
      { id: 'e3', source: 'agent-2', target: 'agent-3' },
      { id: 'e4', source: 'agent-3', target: 'output-1' }
    ],
    entryNodeId: 'trigger-1',
    variables: [
      { id: 'topic', name: 'Research Topic', placeholder: 'What to research', required: true }
    ],
    isBlueprint: true,
    blueprintCategory: 'Research',
    estimatedDuration: '5-15 minutes',
    status: 'active',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'workflow-content-pipeline',
    name: 'Content Creation Pipeline',
    description: 'Research a topic, write content, edit for quality, and produce final output.',
    tier: 'workflow',
    access: 'pro',
    icon: 'PenTool',
    color: 'bg-purple-500',
    category: 'Writing',
    tags: ['content', 'writing', 'multi-step'],
    trigger: { type: 'manual', config: {} },
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 50, y: 150 },
        data: { label: 'Start', description: 'Begin content creation' }
      },
      {
        id: 'agent-1',
        type: 'agent',
        position: { x: 250, y: 150 },
        data: { label: 'Research', agentId: 'agent-research-assistant', description: 'Gather background info' }
      },
      {
        id: 'prompt-1',
        type: 'prompt',
        position: { x: 450, y: 150 },
        data: { 
          label: 'Write Draft', 
          promptTemplate: 'Based on the research, write a comprehensive article about {{topic}}. Target length: {{length}} words.',
          variables: [
            { id: 'topic', name: 'Topic', placeholder: 'Article topic', required: true },
            { id: 'length', name: 'Length', placeholder: '1000', defaultValue: '1000', required: false }
          ]
        }
      },
      {
        id: 'agent-2',
        type: 'agent',
        position: { x: 650, y: 150 },
        data: { label: 'Edit', agentId: 'agent-writing-editor', description: 'Refine and polish' }
      },
      {
        id: 'output-1',
        type: 'output',
        position: { x: 850, y: 150 },
        data: { label: 'Final Article', outputFormat: 'markdown' }
      }
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-1' },
      { id: 'e2', source: 'agent-1', target: 'prompt-1' },
      { id: 'e3', source: 'prompt-1', target: 'agent-2' },
      { id: 'e4', source: 'agent-2', target: 'output-1' }
    ],
    entryNodeId: 'trigger-1',
    isBlueprint: true,
    blueprintCategory: 'Content',
    estimatedDuration: '10-20 minutes',
    status: 'active',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'workflow-debate-analysis',
    name: 'Debate & Analysis',
    description: 'Present an idea, challenge it with devil\'s advocate, then synthesize balanced conclusion.',
    tier: 'workflow',
    access: 'pro',
    icon: 'Scale',
    color: 'bg-red-500',
    category: 'Analysis',
    tags: ['debate', 'critical-thinking', 'analysis'],
    trigger: { type: 'manual', config: {} },
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 50, y: 150 },
        data: { label: 'Start', description: 'Present idea for analysis' }
      },
      {
        id: 'prompt-1',
        type: 'prompt',
        position: { x: 250, y: 100 },
        data: { 
          label: 'Present Case', 
          promptTemplate: 'Present the strongest case for: {{idea}}',
          variables: [{ id: 'idea', name: 'Idea', placeholder: 'Your idea or proposal', required: true }]
        }
      },
      {
        id: 'agent-1',
        type: 'agent',
        position: { x: 250, y: 200 },
        data: { label: 'Challenge', agentId: 'agent-devil-advocate', description: 'Find weaknesses' }
      },
      {
        id: 'agent-2',
        type: 'agent',
        position: { x: 450, y: 150 },
        data: { label: 'Synthesize', agentId: 'agent-synthesizer', description: 'Balanced conclusion' }
      },
      {
        id: 'output-1',
        type: 'output',
        position: { x: 650, y: 150 },
        data: { label: 'Analysis Report', outputFormat: 'markdown' }
      }
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'prompt-1' },
      { id: 'e2', source: 'trigger-1', target: 'agent-1' },
      { id: 'e3', source: 'prompt-1', target: 'agent-2' },
      { id: 'e4', source: 'agent-1', target: 'agent-2' },
      { id: 'e5', source: 'agent-2', target: 'output-1' }
    ],
    entryNodeId: 'trigger-1',
    isBlueprint: true,
    blueprintCategory: 'Analysis',
    estimatedDuration: '5-10 minutes',
    status: 'active',
    createdAt: '2024-01-01',
    createdBy: 'system'
  },
  {
    id: 'workflow-repo-intelligence',
    name: 'GitHub Repository Intelligence',
    description: 'Comprehensive repository analysis: structure, code quality, UI/UX, market positioning, and feature roadmap.',
    tier: 'workflow',
    access: 'pro',
    icon: 'GitBranch',
    color: 'bg-gray-800',
    category: 'Technical',
    tags: ['github', 'repository', 'analysis', 'comprehensive'],
    trigger: { type: 'manual', config: {} },
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 50, y: 200 },
        data: { 
          label: 'Start Analysis', 
          description: 'Input GitHub repository to analyze'
        }
      },
      {
        id: 'agent-structure',
        type: 'agent',
        position: { x: 250, y: 50 },
        data: { label: 'Structure Analysis', agentId: 'agent-structure-analyst', description: 'Analyze architecture and dependencies' }
      },
      {
        id: 'agent-code',
        type: 'agent',
        position: { x: 250, y: 150 },
        data: { label: 'Code Review', agentId: 'agent-code-reviewer', description: 'Review code quality and patterns' }
      },
      {
        id: 'agent-ui',
        type: 'agent',
        position: { x: 250, y: 250 },
        data: { label: 'UI/UX Audit', agentId: 'agent-ui-auditor', description: 'Audit frontend and user experience' }
      },
      {
        id: 'agent-market',
        type: 'agent',
        position: { x: 250, y: 350 },
        data: { label: 'Market Research', agentId: 'agent-market-researcher', description: 'Research competitors and positioning' }
      },
      {
        id: 'agent-features',
        type: 'agent',
        position: { x: 450, y: 200 },
        data: { label: 'Feature Ideation', agentId: 'agent-feature-ideator', description: 'Generate feature roadmap' }
      },
      {
        id: 'agent-strategist',
        type: 'agent',
        position: { x: 650, y: 200 },
        data: { label: 'Synthesize Strategy', agentId: 'agent-repo-strategist', description: 'Compile comprehensive strategy' }
      },
      {
        id: 'output-1',
        type: 'output',
        position: { x: 850, y: 200 },
        data: { label: 'Repository Intelligence Report', outputFormat: 'markdown' }
      }
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-structure' },
      { id: 'e2', source: 'trigger-1', target: 'agent-code' },
      { id: 'e3', source: 'trigger-1', target: 'agent-ui' },
      { id: 'e4', source: 'trigger-1', target: 'agent-market' },
      { id: 'e5', source: 'agent-structure', target: 'agent-features' },
      { id: 'e6', source: 'agent-code', target: 'agent-features' },
      { id: 'e7', source: 'agent-ui', target: 'agent-features' },
      { id: 'e8', source: 'agent-market', target: 'agent-features' },
      { id: 'e9', source: 'agent-structure', target: 'agent-strategist' },
      { id: 'e10', source: 'agent-code', target: 'agent-strategist' },
      { id: 'e11', source: 'agent-ui', target: 'agent-strategist' },
      { id: 'e12', source: 'agent-market', target: 'agent-strategist' },
      { id: 'e13', source: 'agent-features', target: 'agent-strategist' },
      { id: 'e14', source: 'agent-strategist', target: 'output-1' }
    ],
    entryNodeId: 'trigger-1',
    variables: [
      { id: 'repo_owner', name: 'Repository Owner', placeholder: 'e.g., facebook', required: true },
      { id: 'repo_name', name: 'Repository Name', placeholder: 'e.g., react', required: true }
    ],
    isBlueprint: true,
    blueprintCategory: 'Technical',
    estimatedDuration: '10-20 minutes',
    estimatedCost: '$0.50-2.00',
    status: 'active',
    createdAt: '2024-01-01',
    createdBy: 'system'
  }
];

export const ALL_AI_TOOLS = [...QUICK_PROMPTS, ...AGENTS, ...SAMPLE_WORKFLOWS];

export function getAIToolById(id: string) {
  return ALL_AI_TOOLS.find(tool => tool.id === id);
}

export function getAIToolsByTier(tier: 'quick-prompt' | 'agent' | 'workflow') {
  return ALL_AI_TOOLS.filter(tool => tool.tier === tier);
}

export function getAIToolsByCategory(category: string) {
  if (category === 'All') return ALL_AI_TOOLS;
  return ALL_AI_TOOLS.filter(tool => tool.category === category);
}

export function getAgentById(id: string): Agent | undefined {
  return AGENTS.find(agent => agent.id === id);
}

export function getWorkflowById(id: string): Workflow | undefined {
  return SAMPLE_WORKFLOWS.find(workflow => workflow.id === id);
}

export function getWorkflowBlueprints(): Workflow[] {
  return SAMPLE_WORKFLOWS.filter(w => w.isBlueprint);
}
